import { FC, useEffect, useMemo, useState } from 'react'
import classNames from 'classnames'
import moment from 'moment'
import { ReactComponent as GoogleAdsLogo } from 'src/assets/svg/google-ads.svg'
import { ReactComponent as MetaLogo } from 'src/assets/svg/meta-ads.svg'
import {
  AD_PERFORMANCE_FILTER_TABS,
  SmartInsightsAIRecommendations,
} from 'src/charts/SmartInsightsAIRecommendations'
import { ScopedSummarySection } from 'src/charts/SummarySection/ScopedSummarySection'
import { PageBackLink, PageTitle } from 'src/components/PageBackLink'
import { useQueryParams } from 'src/hooks/useQueryParams'
import { GlobalQueryParams } from 'src/interfaces/globalQueryParams.interface'
import {
  useAdPerfAccounts,
  useAdPerfOverview,
  useAdPerfSmartInsights,
} from 'src/reactQuery/useAdPerformance'
import { getDateRangeByQueryParams } from 'src/utils/getDateRangeByQueryParams'
import { ReactComponent as TikTokLogo } from './logos/tiktok-color.svg'
import { ReactComponent as LinkedInLogo } from './logos/linkedin-color.svg'
import { AccountPicker } from './AccountPicker'
import { ActiveTimes } from './ActiveTimes'
import { AdDetailModal } from './AdDetailModal'
import { AudienceAnalysis } from './AudienceAnalysis'
import { Campaigns } from './Campaigns'
import { ConnectModal } from './ConnectModal'
import { Creatives } from './Creatives'
import { PerformanceTrends } from './PerformanceTrends'
import { Placements } from './Placements'
import { PlatformComparison } from './PlatformComparison'
import { Scorecards } from './Scorecards'
import s from './dashboard.module.scss'
import { PlusCircle } from './icons'

const PLATFORMS = [
  { key: 'all', label: 'All' },
  { Logo: MetaLogo, key: 'meta', label: 'Meta' },
  { Logo: GoogleAdsLogo, key: 'google', label: 'Google Ads' },
  { Logo: LinkedInLogo, key: 'linkedin', label: 'LinkedIn' },
  { Logo: TikTokLogo, key: 'tiktok', label: 'TikTok' },
]

const Breadcrumb: FC = () => (
  <div className={s.breadcrumb}>
    <PageBackLink label="Ad Analysis" />
    <PageTitle>Ad Performance</PageTitle>
  </div>
)

export const AdPerformanceDashboard: FC = () => {
  const { data: accounts, isLoading } = useAdPerfAccounts()
  const [accountId, setAccountId] = useState('')
  const [connectOpen, setConnectOpen] = useState(false)
  const [openAd, setOpenAd] = useState<string | null>(null)
  const { queryParams } = useQueryParams<GlobalQueryParams>()
  const dates = getDateRangeByQueryParams(queryParams)

  useEffect(() => {
    if (!accountId && accounts?.length) setAccountId(accounts[0].account_id)
  }, [accounts, accountId])

  const account = accounts?.find((a) => a.account_id === accountId)
  const range = useMemo(
    () => ({ accountId, fromDate: dates?.start, toDate: dates?.end }),
    [accountId, dates?.start, dates?.end]
  )
  const { data: overview } = useAdPerfOverview(range)
  const { data: smartInsights, isLoading: insightsLoading } =
    useAdPerfSmartInsights(accountId)

  if (isLoading) return <div className={s.page} />

  if (!accounts?.length) {
    return (
      <div className={s.page}>
        <Breadcrumb />
        <div className={s.emptyPage}>
          <h2>Connect your ad accounts to get started</h2>
          <p>
            Bring your campaigns from Meta, Google, LinkedIn and more to track
            performance, analyze results, and unlock actionable insights — all
            in one place.
          </p>
          <button
            className={s.primaryButton}
            onClick={() => setConnectOpen(true)}
            type="button"
          >
            Connect Accounts
          </button>
        </div>
        <ConnectModal
          isOpen={connectOpen}
          onClose={() => setConnectOpen(false)}
        />
      </div>
    )
  }

  const currency = account?.currency ?? 'USD'

  return (
    <div className={s.page}>
      <div>
        <Breadcrumb />
        <div className={s.header}>
          <div>
            <AccountPicker
              accounts={accounts}
              onChange={setAccountId}
              value={accountId}
            />
            <div className={s.syncNote}>
              {account?.status === 'reauth'
                ? 'Access to this account was lost. Ask the owner to share it again.'
                : !account?.synced
                  ? 'Syncing the last 90 days — numbers fill in over the next minutes.'
                  : account?.last_sync_at
                    ? `Updated ${moment(account.last_sync_at).fromNow()} · ${currency} · ${account.timezone}`
                    : ''}
            </div>
          </div>
          <div className={s.tabs}>
            {PLATFORMS.map((p) => (
              <button
                className={classNames(s.tab, {
                  [s.tabActive]: p.key === 'meta',
                })}
                disabled={p.key !== 'meta'}
                key={p.key}
                title={p.key === 'meta' || p.key === 'all' ? '' : 'Coming soon'}
                type="button"
              >
                {p.label}
                {p.Logo && <p.Logo className={s.tabLogo} />}
              </button>
            ))}
            <button
              className={s.addAccount}
              onClick={() => setConnectOpen(true)}
              type="button"
            >
              Add Account <PlusCircle />
            </button>
          </div>
        </div>
      </div>

      {overview && !overview.has_data && (
        <div className={classNames(s.card, s.empty)}>
          No ad delivery in {overview.window.since} – {overview.window.until}.
          Try a longer date range.
        </div>
      )}

      <Scorecards data={overview} />
      <PerformanceTrends currency={currency} range={range} />
      <div className={s.row2}>
        <ActiveTimes currency={currency} range={range} />
        <AudienceAnalysis currency={currency} range={range} />
      </div>
      <div className={s.row2}>
        <PlatformComparison currency={currency} range={range} />
        <Placements currency={currency} range={range} />
      </div>
      <Campaigns currency={currency} range={range} />
      <Creatives currency={currency} onOpenAd={setOpenAd} range={range} />
      {/* Generated per period by smart-insights, not for the picked range. */}
      <SmartInsightsAIRecommendations
        data={{
          hasInsights: !!smartInsights?.insights.length,
          insights: smartInsights?.insights,
        }}
        filterTabs={AD_PERFORMANCE_FILTER_TABS}
        homePageVariant
        isLoading={insightsLoading}
      />
      {accountId && (
        <ScopedSummarySection
          scope={{ id: accountId, kind: 'adPerformance' }}
        />
      )}

      <AdDetailModal
        accountName={account?.name}
        adId={openAd}
        currency={currency}
        onClose={() => setOpenAd(null)}
        range={range}
      />
      <ConnectModal
        isOpen={connectOpen}
        onClose={() => setConnectOpen(false)}
      />
    </div>
  )
}
